import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import { useModal } from '../../contexts/ModalContext';
import { dbInsert } from '../../lib/supabase';

const ConsultationModal = () => {
    const { isConsultationOpen, closeConsultation } = useModal();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        projectBrief: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Save to Supabase / Fallback local storage
            await dbInsert('consultations', {
                ...formData,
                status: 'new',
                source: 'global_modal'
            });
            // Construct Gmail Web compose link
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=bytepairs2026@gmail.com&su=${encodeURIComponent(`New Project Estimate Request from ${formData.name}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nCustomer Email: ${formData.email}\n\nProject Brief:\n${formData.projectBrief}`)}`;

            setSubmitSuccess(true);
            
            // Open Gmail Web Compose in a new tab
            window.open(gmailUrl, '_blank');

            setFormData({ name: '', email: '', projectBrief: '' });
            setTimeout(() => {
                setSubmitSuccess(false);
                closeConsultation();
            }, 3000);
        } catch (error) {
            console.error("Modal submission error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isConsultationOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeConsultation}
                        className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden"
                    >
                        <button
                            onClick={closeConsultation}
                            className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors z-10"
                        >
                            <Icon name="X" size={20} />
                        </button>

                        <div className="grid md:grid-cols-5 h-full">
                            {/* Left Sidebar (Desktop) */}
                            <div className="hidden md:flex md:col-span-2 bg-gradient-to-br from-primary/20 via-accent/20 to-primary/20 p-8 flex-col justify-between relative overflow-hidden">
                                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
                                <div className="relative z-10">
                                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                                        <Icon name="Rocket" size={24} className="text-primary" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-4 leading-tight">Project Strategy Session</h3>
                                    <p className="text-slate-300 text-sm leading-relaxed mb-6">
                                        Get a professional architectural overview and estimate in under 30 minutes.
                                    </p>
                                    <ul className="space-y-3">
                                        {['Architectural Review', 'Tech Stack Choice', 'Timeline & ROI'].map((item, i) => (
                                            <li key={i} className="flex items-center space-x-2 text-xs font-medium text-slate-200">
                                                <Icon name="Check" size={12} className="text-emerald-500" />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="relative z-10 p-4 bg-white/5 rounded-2xl border border-white/10 text-[10px] text-slate-400">
                                    <p>Our engineers respond within 2 business hours. Guaranteed.</p>
                                </div>
                            </div>

                            {/* Main Form Area */}
                            <div className="col-span-1 md:col-span-3 p-8 md:p-10">
                                {submitSuccess ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center py-10">
                                        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
                                            <Icon name="CheckCircle" size={40} className="text-emerald-500" />
                                        </div>
                                        <h4 className="text-2xl font-bold text-white mb-2">Request Received!</h4>
                                        <p className="text-slate-400">We're reviewing your requirements now. Check your inbox shortly.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-8">
                                            <h4 className="text-2xl font-bold text-white mb-1">Start Your Project</h4>
                                            <p className="text-sm text-slate-400">Tell us what you're imagining.</p>
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-1">Name</label>
                                                <input
                                                    required
                                                    type="text"
                                                    placeholder="John Smith"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-3 text-white placeholder:text-slate-600 focus:border-primary/50 outline-none transition-all"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-1">Company Email</label>
                                                <input
                                                    required
                                                    type="email"
                                                    placeholder="john@company.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-3 text-white placeholder:text-slate-600 focus:border-primary/50 outline-none transition-all"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-1">Project Brief</label>
                                                <textarea
                                                    required
                                                    placeholder="E-commerce app for luxury brands..."
                                                    rows={3}
                                                    value={formData.projectBrief}
                                                    onChange={(e) => setFormData({ ...formData, projectBrief: e.target.value })}
                                                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-3 text-white placeholder:text-slate-600 focus:border-primary/50 outline-none transition-all resize-none"
                                                ></textarea>
                                            </div>

                                            <button
                                                disabled={isSubmitting}
                                                className="w-full mt-4 bg-gradient-to-r from-primary to-accent py-4 rounded-xl font-bold text-white shadow-brand hover:shadow-brand-lg transform hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                                            >
                                                {isSubmitting ? (
                                                    <Icon name="Loader2" className="animate-spin" size={18} />
                                                ) : (
                                                    <span>Get My Free Estimate</span>
                                                )}
                                            </button>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConsultationModal;
